import { DEFAULT_LOCALE, type Locale } from './locale';

/**
 * Formatação de data, número e moeda em UM lugar, sensível ao idioma.
 *
 * Antes havia 69 pontos com locale fixo em 'pt-BR' espalhados pelo app. Além de
 * impedir tradução, isso escondia dois problemas reais:
 *
 *  1. `Intl.NumberFormat('es', { currency: 'BRL' })` imprime `1234,50 BRL`, sem
 *     símbolo e sem separador de milhar. Precisa de `narrowSymbol`.
 *  2. `es` puro é a Espanha (símbolo posposto, agrupamento diferente). O
 *     mercado hispânico do MeuGantt é a América Latina, então o locale de
 *     FORMATAÇÃO é `es-419`, separado do locale de INTERFACE (`es`).
 *
 * Por isso existe FORMAT_LOCALE: o idioma que o usuário lê e o locale que
 * formata número não são a mesma coisa.
 */
export const FORMAT_LOCALE: Record<Locale, string> = {
  'pt-BR': 'pt-BR',
  en: 'en-US',
  es: 'es-419',
};

/**
 * Moeda de exibição. Continua BRL em todos os idiomas porque é o que o Stripe
 * cobra de fato: mostrar dólar para quem vai ser cobrado em real seria mentir
 * no preço. Só a formatação do número segue o idioma.
 */
export const DISPLAY_CURRENCY = 'BRL';

/**
 * Idioma corrente, mantido pelo LocaleProvider.
 *
 * Formatação acontece em dezenas de lugares, muitos deles helpers de módulo que
 * não podem chamar hook. Exigir `useFormat()` em cada um espalharia o problema e
 * deixaria buracos silenciosos (um helper esquecido volta a formatar em pt-BR
 * sem ninguém notar).
 *
 * Não ser reativo por si só não é problema aqui: a subárvore autenticada é
 * remontada por `key={locale}` na troca de idioma, então tudo re-renderiza
 * depois que este valor já mudou.
 */
let currentLocale: Locale = DEFAULT_LOCALE;

export function setCurrentLocale(locale: Locale): void {
  currentLocale = locale;
}

export function getCurrentLocale(): Locale {
  return currentLocale;
}

const toDate = (value: Date | string | number | null | undefined): Date | null => {
  if (value === null || value === undefined || value === '') return null;
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
};

/** Data curta (04/03/2026, 3/4/2026, 04/03/2026). Vazio se a entrada não é data. */
export function formatDate(
  value: Date | string | number | null | undefined,
  locale: Locale = currentLocale,
  options?: Intl.DateTimeFormatOptions,
): string {
  const d = toDate(value);
  return d ? d.toLocaleDateString(FORMAT_LOCALE[locale], options) : '';
}

/** Data e hora, para logs e carimbos de atividade. */
export function formatDateTime(
  value: Date | string | number | null | undefined,
  locale: Locale = currentLocale,
): string {
  const d = toDate(value);
  return d ? d.toLocaleString(FORMAT_LOCALE[locale]) : '';
}

/** Só a hora (14:32). */
export function formatTime(
  value: Date | string | number | null | undefined,
  locale: Locale = currentLocale,
): string {
  const d = toDate(value);
  return d ? d.toLocaleTimeString(FORMAT_LOCALE[locale], { hour: '2-digit', minute: '2-digit' }) : '';
}

export function formatNumber(
  value: number | null | undefined,
  locale: Locale = currentLocale,
  options?: Intl.NumberFormatOptions,
): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  return new Intl.NumberFormat(FORMAT_LOCALE[locale], options).format(value);
}

/**
 * Moeda. `narrowSymbol` é obrigatório: sem ele, es-419 com BRL imprime o código
 * "BRL" em vez de "R$".
 */
export function formatCurrency(
  value: number | null | undefined,
  locale: Locale = currentLocale,
  currency: string = DISPLAY_CURRENCY,
): string {
  if (typeof value !== 'number' || !Number.isFinite(value)) return '';
  return new Intl.NumberFormat(FORMAT_LOCALE[locale], {
    style: 'currency',
    currency,
    currencyDisplay: 'narrowSymbol',
  }).format(value);
}

/** Nomes de mês e de dia da semana, do próprio Intl (nada de array hardcoded). */
export function monthNames(locale: Locale = currentLocale, style: 'long' | 'short' = 'long'): string[] {
  const fmt = new Intl.DateTimeFormat(FORMAT_LOCALE[locale], { month: style });
  return Array.from({ length: 12 }, (_, m) => fmt.format(new Date(2026, m, 1)));
}

/** Domingo primeiro, para casar com `Date.getDay()`. */
export function weekdayNames(locale: Locale = currentLocale, style: 'long' | 'short' | 'narrow' = 'short'): string[] {
  const fmt = new Intl.DateTimeFormat(FORMAT_LOCALE[locale], { weekday: style });
  // 2026-02-01 é um domingo.
  return Array.from({ length: 7 }, (_, d) => fmt.format(new Date(2026, 1, 1 + d)));
}

/**
 * Plural pelo `Intl.PluralRules` nativo, sem dependência.
 *
 * Substitui os ternários de sufixo (`${n} tarefa${n > 1 ? 's' : ''}`), que só
 * funcionam para português e quebram em qualquer idioma com regra diferente, e
 * as gambiarras invariantes tipo "tarefa(s)".
 *
 * Uso: `plural(n, { one: 'tarefa', other: 'tarefas' }, locale)`.
 */
export type PluralForms = { zero?: string; one: string; two?: string; few?: string; many?: string; other: string };

export function plural(count: number, forms: PluralForms, locale: Locale = currentLocale): string {
  // ARMADILHA DO CLDR: em português a regra é `one: i = 0..1`, ou seja, o Intl
  // classifica ZERO como singular e produziria "0 tarefa" na tela. Em inglês
  // zero é plural. Como a expectativa do usuário brasileiro é "0 tarefas" (ou
  // "Nenhuma tarefa"), quem escreve a mensagem pode passar uma forma `zero`
  // explícita, e ela vence a regra da biblioteca.
  if (count === 0 && forms.zero !== undefined) return forms.zero;
  const category = new Intl.PluralRules(FORMAT_LOCALE[locale]).select(count);
  return forms[category as keyof PluralForms] ?? forms.other;
}

/** Açúcar para o caso mais comum: número seguido da forma correta. */
export function pluralize(count: number, forms: PluralForms, locale: Locale = currentLocale): string {
  return `${formatNumber(count, locale)} ${plural(count, forms, locale)}`;
}

/** Ordenação sensível a acento. `.sort()` cru compara code unit e erra com "Á". */
export function collator(locale: Locale = currentLocale): Intl.Collator {
  return new Intl.Collator(FORMAT_LOCALE[locale], { sensitivity: 'base', numeric: true });
}


/*
 * Atalhos usados nos componentes. Como o idioma vem do estado de módulo, o
 * segundo parâmetro aqui são as OPÇÕES, não o locale: é a assinatura que o call
 * site quer (`fmt.date(d, { day: '2-digit' })`).
 */
export const date = (v: Date | string | number | null | undefined, o?: Intl.DateTimeFormatOptions) =>
  formatDate(v, currentLocale, o);

export const dateTime = (v: Date | string | number | null | undefined) => formatDateTime(v, currentLocale);

export const time = (v: Date | string | number | null | undefined) => formatTime(v, currentLocale);

export const number = (v: number | null | undefined, o?: Intl.NumberFormatOptions) =>
  formatNumber(v, currentLocale, o);

export const currency = (v: number | null | undefined, c?: string) => formatCurrency(v, currentLocale, c);
